import React, { useState, useEffect, useRef, useMemo } from "react"; // Ajout de useMemo
import { Row, Col, Card, CardBody, Form, Label, Button } from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";

import {
  getEvaluations,
  getCourseComponents,
  getStudents,
  getAcademicYears,
  getSemesters,
  getStudyLevels
} from "store/actions";

const SearchAnonymousExamRecord = ({ onSearch }) => {
  const dispatch = useDispatch();

  const [filters, setFilters] = useState({
    evaluation_id: null,
    course_component_id: null,
    student_id: null,
    academic_year_id: null,
    semester_id: null,
    study_level_id: null
  });

  const {
    evaluations,
    courseComponents,
    students,
    academicYears,
    semesters,
    studyLevels
  } = useSelector((state) => ({
    evaluations: state.evaluationReducer.evaluations || [],
    courseComponents: state.courseComponentReducer.courseComponents || [],
    students: state.studentReducer.students || [],
    academicYears: state.academicYearReducer.academicYears || [],
    semesters: state.semesterReducer.semesters || [],
    studyLevels: state.studyLevelReducer.studyLevels || []
  }));

  // Charger les données pour les listes déroulantes
  useEffect(() => {
    dispatch(getEvaluations());
    dispatch(getCourseComponents());
    dispatch(getStudents());
    dispatch(getAcademicYears());
    dispatch(getSemesters());
    dispatch(getStudyLevels());
  }, [dispatch]);

  // Options spécifiques pour les EC avec formatage "Nom — Semestre"
  // et filtrage par semestre si sélectionné
  const ecOptions = useMemo(() => {
    let list = courseComponents;
    
    // Filtrage si un semestre est sélectionné dans le filtre semestre
    if (filters.semester_id) {
      list = courseComponents.filter(ec => ec.semester_id === filters.semester_id.value);
    }

    return list.map((ec) => ({
      value: ec.id,
      label: `${ec.title} — ${ec.semester?.code || "N/A"}`
    }));
  }, [courseComponents, filters.semester_id]);

  const selectOptions = (list, labelKey = "title") =>
    list.map((item) => ({ value: item.id, label: item[labelKey] }));

  const handleSelectChange = (name, selected) => {
    setFilters((prev) => ({ ...prev, [name]: selected }));
  };

  const debouncedSearchRef = useRef(
    debounce((f) => {
      const payload = {};
      Object.keys(f).forEach((key) => {
        payload[key] = f[key]?.value || "";
      });
      onSearch(payload);
    }, 500)
  );

  useEffect(() => {
    debouncedSearchRef.current(filters);
  }, [filters]);

  return (
    <Row>
      <Col lg={12}>
        <Card className="anonymous-exam-record-filter">
          <CardBody>
            <Form>
              <Row className="g-3">
                <Col xxl={3} lg={3}>
                  <Label>Évaluation</Label>
                  <Select
                    options={selectOptions(evaluations, "title")}
                    value={filters.evaluation_id}
                    onChange={(v) => handleSelectChange("evaluation_id", v)}
                    isClearable
                    placeholder="Toutes"
                  />
                </Col>

                <Col xxl={3} lg={3}>
                  <Label>EC</Label>
                  <Select
                    /* ✅ Utilisation de la variable ecOptions formatée */
                    options={ecOptions} 
                    value={filters.course_component_id}
                    onChange={(v) => handleSelectChange("course_component_id", v)}
                    isClearable
                    placeholder="Tous"
                  />
                </Col>

                <Col xxl={3} lg={3}>
                  <Label>Étudiant</Label>
                  <Select
                    options={students.map((s) => ({
                      value: s.id,
                      label: `${s.first_name} ${s.last_name} (${s.student_number})`
                    }))}
                    value={filters.student_id}
                    onChange={(v) => handleSelectChange("student_id", v)}
                    isClearable
                    placeholder="Tous"
                  />
                </Col>

                <Col xxl={3} lg={3}>
                  <Label>Année académique</Label>
                  <Select
                    options={selectOptions(academicYears, "name")}
                    value={filters.academic_year_id}
                    onChange={(v) => handleSelectChange("academic_year_id", v)}
                    isClearable
                    placeholder="Toutes"
                  />
                </Col>

                <Col xxl={3} lg={3}>
                  <Label>Semestre</Label>
                  <Select
                    options={selectOptions(semesters, "code")}
                    value={filters.semester_id}
                    onChange={(v) => handleSelectChange("semester_id", v)}
                    isClearable
                    placeholder="Tous"
                  />
                </Col>

                <Col xxl={3} lg={3}>
                  <Label>Niveau</Label>
                  <Select
                    options={selectOptions(studyLevels, "name")}
                    value={filters.study_level_id}
                    onChange={(v) => handleSelectChange("study_level_id", v)}
                    isClearable
                    placeholder="Tous"
                  />
                </Col>
              </Row>

              <Row className="g-3 mt-3">
                <Col xxl={3} lg={4}>
                  <Button
                    color="primary"
                    type="button"
                    onClick={() => debouncedSearchRef.current.flush()}
                  >
                    Rechercher
                  </Button>
                </Col>
              </Row>

            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default SearchAnonymousExamRecord;